import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getFinancialSummary } from 'api/account';
import * as leadActions from 'containers/Leads/actions';
import * as projectsActions from 'containers/Projects/actions';
import * as actions from 'containers/App/actions';
import { getInvoiceTemplates } from 'containers/Settings/Invoices/actions';
import Avatar from 'components/Avatar';
import { imagePickSize } from 'utils/general';
import dayjs from 'dayjs';
import { usePrefs } from 'hooks/usePrefs';
import { useHistory, Redirect } from 'react-router-dom';
import { getWidgetsForSection, DEFAULT_WIDGET_ORDER } from './widgetConfig';
import SortableWidget from './SortableWidget';
import HiddenWidgetPlaceholder from './HiddenWidgetPlaceholder';
import './pro-dashboard.scss';
import './widget-settings.scss';
import './widget-visibility-toggle.scss';
import './hidden-widget-placeholder.scss';

const ProDashboard = () => {
  const userInfo = useSelector((state) => state.global.userInfo);
  const { firstName, initials, profileImage, organization, orgId } = userInfo;
  const { isDisabled, isPaused, isCancel, isPendingCancellation, personas } = userInfo;
  const { hasUnpausedProjects } = personas || {};

  // Mirrors NavBar isActive (components/NavBar/NavBar.js) so the dashboard and the nav
  // gate on the same rule. Users on someone else's active projects keep access.
  const isAccountActive = !isDisabled && !isPaused && (!isCancel || isPendingCancellation);
  const shouldRedirectToHold = !isAccountActive && !hasUnpausedProjects;

  const { activeProjects, projectsLimit } = useSelector((state) => state.projects);
  const tasksInfo = useSelector((state) => state.global.tasksInfo);
  const filters = useSelector(state => state.leads.filters);
  const notificationsOpen = useSelector(state => state.global.notificationCenterOpen);

  const dispatch = useDispatch();
  const history = useHistory();

  const { getLeadsFilters } = bindActionCreators(leadActions, dispatch);
  const { getProjectLimits } = bindActionCreators(projectsActions, dispatch);
  const { setNotificationsCenterOpen, setNotificationsCenterTab } = bindActionCreators(actions, dispatch);

  const toggleNotifications = () => {
    setNotificationsCenterTab('allNotifications');
    setNotificationsCenterOpen(!notificationsOpen);
  };

  const toggleUrgentTasks = () => {
    setNotificationsCenterTab('allMyTasks');
    setNotificationsCenterOpen(!notificationsOpen);
  };

  // Get user preferences for widget order and visibility
  const { preferences, updatePrefByKey } = usePrefs();
  const widgetOrder = preferences?.['dashboard.widgetOrder'] || DEFAULT_WIDGET_ORDER;
  const widgetVisibility = preferences?.['dashboard.widgetVisibility'] || {};
  const [isDragEnabled, setIsDragEnabled] = useState(false);
  const [financialInfo, setFinancialInfo] = useState(null);
  const [welcomeMessage, setWelcomeMessage] = useState(null);
  const [currency, setCurrency] = useState(organization?.currency || 'USD');

  let imagePath;

  if (profileImage) {
    const relativePath = profileImage?.resolutions?.circle108_5 ?
      imagePickSize(profileImage, 'circle108_5') :
      imagePickSize(profileImage, 'full');
    imagePath = `//${process.env.REACT_APP_AWS_CLOUDFRONT}/${relativePath}`;
  }

  // Prepare props for widgets
  const widgetProps = useMemo(() => {
    return {
      organization,
      orgId,
      projectsLimit,
      activeProjects,
      tasksInfo,
      userInfo,
      filters,
      financialInfo,
      currency,
      setCurrency,
    };
  }, [filters, userInfo, tasksInfo, financialInfo, currency, setCurrency]);

  // Get visible widgets for each section (include hidden widgets when in drag mode)
  const headerWidgets = useMemo(
    () => getWidgetsForSection('header', userInfo, widgetOrder, widgetVisibility, isDragEnabled),
    [userInfo, widgetOrder, widgetVisibility, isDragEnabled],
  );

  const topWidgets = useMemo(
    () => getWidgetsForSection('top', userInfo, widgetOrder, widgetVisibility, isDragEnabled),
    [userInfo, widgetOrder, widgetVisibility, isDragEnabled],
  );

  const bodyWidgets = useMemo(
    () => getWidgetsForSection('body', userInfo, widgetOrder, widgetVisibility, isDragEnabled),
    [userInfo, widgetOrder, widgetVisibility, isDragEnabled],
  );

  // Setup drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Handle drag end
  const handleDragEnd = useCallback((event, section) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const widgets = section === 'top' ? topWidgets : bodyWidgets;
      const oldIndex = widgets.findIndex(w => w.id === active.id);
      const newIndex = widgets.findIndex(w => w.id === over.id);

      const reorderedWidgets = arrayMove(widgets, oldIndex, newIndex);
      const newOrder = reorderedWidgets.map(w => w.id);

      // Update preferences
      const updatedWidgetOrder = {
        ...widgetOrder,
        [section]: newOrder,
      };
      updatePrefByKey('dashboard.widgetOrder', updatedWidgetOrder);
    }
  }, [topWidgets, bodyWidgets, widgetOrder, updatePrefByKey]);

  // Handle widget visibility toggle
  const handleToggleVisibility = useCallback((widgetId, isVisible) => {
    const updatedVisibility = {
      ...widgetVisibility,
      [widgetId]: isVisible,
    };
    updatePrefByKey('dashboard.widgetVisibility', updatedVisibility);
  }, [widgetVisibility, updatePrefByKey]);

  // Render a widget section
  const renderWidgetSection = (widgets, section, strategy) => {
    if (widgets.length === 0) return null;

    const showTitleBar = section === 'body';

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(event) => handleDragEnd(event, section)}
      >
        <SortableContext
          items={widgets.map(w => w.id)}
          strategy={strategy}
        >
          {widgets.map((widget) => {
            const isVisible = widgetVisibility[widget.id] ?? widget.defaultVisible;

            // If widget is hidden and we're in drag mode, show placeholder
            if (!isVisible && isDragEnabled) {
              return (
                <SortableWidget
                  key={widget.id}
                  id={widget.id}
                  widgetName={widget.name}
                  isDragEnabled={isDragEnabled}
                  isVisible={isVisible}
                  onToggleVisibility={(visible) => handleToggleVisibility(widget.id, visible)}
                  showTitleBar={showTitleBar}
                  size={widget.size}
                  titleAccessory={widget.titleAccessory?.(widgetProps)}
                >
                  <HiddenWidgetPlaceholder
                    widgetName={widget.name}
                    onShow={() => handleToggleVisibility(widget.id, true)}
                  />
                </SortableWidget>
              );
            }

            // Render normal widget
            const WidgetComponent = widget.component;
            const componentProps = widget.mapProps(widgetProps);

            const expandPathMethods = {
              dispatch,
              history: history.push,
              window: (path) => { window.location.href = path; },
              toggleNotifications,
              toggleUrgentTasks,
            };

            const expandPathFnParams = {
              orgId,
            };

            return (
              <SortableWidget
                key={widget.id}
                id={widget.id}
                widgetName={widget.name}
                isDragEnabled={isDragEnabled}
                isVisible={isVisible}
                onToggleVisibility={(visible) => handleToggleVisibility(widget.id, visible)}
                showTitleBar={showTitleBar}
                size={widget.size}
                titleAccessory={widget.titleAccessory?.(widgetProps)}
                onExpand={() => {
                  const method = widget.path[0];
                  const pathFn = widget.path[1];

                  if (typeof expandPathMethods[method] === 'function') {
                    expandPathMethods[method](pathFn(expandPathFnParams));
                  }
                }}
              >
                <WidgetComponent {...componentProps} />
              </SortableWidget>
            );
          })}
        </SortableContext>
      </DndContext>
    );
  };

  async function getFinancialInfo() {
    const params = {
      interval: 'year',
      year: dayjs().get('year'),
      currency,
    };

    const response = await getFinancialSummary(params);

    if (!response.ok) return;

    setFinancialInfo(response.data);
  }

  useEffect(() => {
    // Don't fetch dashboard data for a user we're about to redirect off the dashboard.
    if (shouldRedirectToHold) return;

    // Check lead filters,
    // project limits,
    // and financial info
    if (filters.length === 0) {
      getLeadsFilters();
    }
    getProjectLimits();
    dispatch(getInvoiceTemplates({ orgId }));

    // Calculate welcome message
    const today = dayjs();
    const hour = today.hour();
    let message;
    if (hour >= 5 && hour < 12) {
      message = 'Good morning';
    }
    if (hour >= 12 && hour < 17) {
      message = 'Good afternoon';
    }
    if (hour >= 17 && hour < 21) {
      message = 'Good evening';
    }
    if (hour >= 21 || hour < 5) {
      message = 'Hello';
    }
    setWelcomeMessage(message);
  }, []);

  useEffect(() => {
    if (shouldRedirectToHold) return;

    getFinancialInfo();
  }, [currency]);

  if (shouldRedirectToHold) {
    return <Redirect to="/app/v2/account-on-hold" />;
  }

  return (
    <div className={`dashboard--wrapper container ${isDragEnabled ? 'drag-mode-enabled' : ''}`}>
      <div className="dashboard--section__header user-welcome">
        <div className="welcome">
          <div className="welcome__avatar">
            <Avatar
              photo={imagePath}
              text={imagePath ? '' : initials}
              size={85}
              borderSize={3}
            />
          </div>
          {
            welcomeMessage && (
              <h1>
                <span className="dashboard-welcome-heading__salutation">{welcomeMessage},</span>
                {' '}
                <span className="dashboard-welcome-heading__name">{firstName}.</span>
              </h1>
            )
          }
        </div>
        <div className="dashboard--header-widgets">
          {renderWidgetSection(headerWidgets, 'header', horizontalListSortingStrategy)}
        </div>
      </div>

      <div className="dashboard--section__top">
        {renderWidgetSection(topWidgets, 'top', horizontalListSortingStrategy)}
      </div>

      <div className="dashboard--section__body">
        {renderWidgetSection(bodyWidgets, 'body', verticalListSortingStrategy)}
      </div>

      <button
        className={`dashboard-settings-button ${isDragEnabled ? 'active' : ''}`}
        onClick={() => setIsDragEnabled(!isDragEnabled)}
        aria-label={isDragEnabled ? 'Exit edit mode' : 'Edit dashboard'}
        title={isDragEnabled ? 'Click to exit edit mode' : 'Click to edit dashboard'}
      >
        {
          isDragEnabled ?
            <FontAwesomeIcon icon={['fa', 'check']} /> :
            <FontAwesomeIcon icon={['fa', 'cog']} />
        }
      </button>
    </div>
  );
};

export default ProDashboard;
